/**
 * Typed REST client for the Discordo API.
 *
 * Everything the SPA sends goes through `apiFetch`, so four rules hold in one
 * place: the session cookie always travels, a refusal always arrives as an
 * `ApiError` instead of a silent `Response`, a lost session is reported once to
 * whoever is in charge of showing the login screen, and an unconfirmed address
 * is reported the same way to whoever shows the confirmation screen.
 *
 * Those last two are session-wide facts, not facts about the call that happened
 * to discover them: `401` and `403 email_not_verified` mean the same thing to
 * the app no matter which route answered. Reporting them from here is what keeps
 * every caller from having to recognise them, and keeps the app from showing a
 * bare error where a screen with a way out belongs.
 *
 * The API answers every refusal with `{ error, message }`, and `message` is
 * already written in Brazilian Portuguese for the user — the pages render it
 * verbatim.
 */

/** Same shape as `RequestInit`, except the body is given as a value to encode. */
export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export class ApiError extends Error {
  /** HTTP status, or `0` when the request never reached the server. */
  readonly status: number;
  /** Machine-readable code from the API (`invite_expired`, `unauthenticated`, …). */
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const NETWORK_ERROR_MESSAGE = 'Não foi possível falar com o servidor. Verifique sua conexão.';
const UNEXPECTED_ERROR_MESSAGE = 'O servidor respondeu de forma inesperada.';

/** The API's own code for "the address on this account was never confirmed". */
const EMAIL_NOT_VERIFIED_CODE = 'email_not_verified';

let unauthorizedHandler: (() => void) | null = null;
let emailNotVerifiedHandler: (() => void) | null = null;

/**
 * Registers what to do when the API reports that there is no valid session.
 *
 * The auth store installs a handler that drops the current user, which is what
 * sends the app back to the login screen. Pass `null` to remove it.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * Registers what to do when the API refuses because the address was never
 * confirmed.
 *
 * The auth store installs a handler that marks the signed-in account pending,
 * which is what puts the "confirm your e-mail" screen up — with the address and
 * the resend button — instead of an error nobody can act on. Pass `null` to
 * remove it.
 *
 * It is bound to the code and not to the status: a plain `403` is "this is not
 * yours", and sending that to the confirmation screen would be a lie.
 */
export function setEmailNotVerifiedHandler(handler: (() => void) | null): void {
  emailNotVerifiedHandler = handler;
}

interface ApiErrorBody {
  error?: unknown;
  message?: unknown;
}

/** Reads the response body, tolerating the empty ones (204 and friends). */
async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function errorFrom(status: number, payload: unknown): ApiError {
  const body: ApiErrorBody = typeof payload === 'object' && payload !== null ? payload : {};
  const code = typeof body.error === 'string' ? body.error : 'unexpected_error';
  const message = typeof body.message === 'string' ? body.message : UNEXPECTED_ERROR_MESSAGE;
  return new ApiError(status, code, message);
}

export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const { body, headers, ...rest } = init;
  const requestHeaders = new Headers(headers);

  const request: RequestInit = { ...rest, credentials: 'include', headers: requestHeaders };
  if (body !== undefined) {
    requestHeaders.set('content-type', 'application/json');
    request.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(path, request);
  } catch {
    throw new ApiError(0, 'network_error', NETWORK_ERROR_MESSAGE);
  }

  const payload = await readBody(response);

  if (!response.ok) {
    const error = errorFrom(response.status, payload);

    if (error.status === 401) {
      unauthorizedHandler?.();
    } else if (error.status === 403 && error.code === EMAIL_NOT_VERIFIED_CODE) {
      emailNotVerifiedHandler?.();
    }

    // The refusal is still a refusal: the handler changes which screen is up,
    // it does not invent an answer the server declined to give.
    throw error;
  }

  return payload as T;
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export interface ApiUploadInit {
  /** The file itself; it travels in the multipart part named `file`. */
  file: File;
  /** Text fields sent alongside it — the caption typed next to the file. */
  fields?: Record<string, string>;
  /** Called with how much of the body has left the browser, from 0 to 1. */
  onProgress?: (fraction: number) => void;
}

/**
 * Sends one file as `multipart/form-data`, reporting how far along it is.
 *
 * `XMLHttpRequest` instead of `fetch` for exactly one reason: `fetch` says
 * nothing at all while a request body is being uploaded. Twenty-five megabytes
 * with no progress on screen is indistinguishable from an app that froze, and
 * the person deciding whether to wait or to reload deserves better than a
 * guess.
 *
 * Everything else matches `apiFetch` on purpose — the session cookie travels,
 * a refusal arrives as an `ApiError`, and a lost session or an unconfirmed
 * address is reported to whoever owns the screen that fixes it — so a caller
 * handles an upload exactly like any other call.
 */
export function apiUpload<T>(path: string, init: ApiUploadInit): Promise<T> {
  const body = new FormData();
  for (const [name, value] of Object.entries(init.fields ?? {})) {
    body.append(name, value);
  }
  // Last, so a parser reading the parts in order has the fields before the
  // bytes: the API reads the caption off the part it is already holding.
  body.append('file', init.file);

  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', path);
    request.withCredentials = true;

    request.upload.onprogress = (event) => {
      // A body of unknown length reports no total; a fraction computed from it
      // would be `NaN` and would render as an empty bar.
      if (event.lengthComputable && event.total > 0) {
        init.onProgress?.(event.loaded / event.total);
      }
    };

    request.onload = () => {
      let payload: unknown;
      try {
        payload = request.responseText === '' ? undefined : JSON.parse(request.responseText);
      } catch {
        payload = undefined;
      }

      if (request.status >= 200 && request.status < 300) {
        resolve(payload as T);
        return;
      }

      const error = errorFrom(request.status, payload);
      if (error.status === 401) {
        unauthorizedHandler?.();
      } else if (error.status === 403 && error.code === EMAIL_NOT_VERIFIED_CODE) {
        emailNotVerifiedHandler?.();
      }
      reject(error);
    };

    request.onerror = () => {
      reject(new ApiError(0, 'network_error', NETWORK_ERROR_MESSAGE));
    };

    request.onabort = () => {
      reject(new ApiError(0, 'upload_aborted', 'O envio do arquivo foi cancelado.'));
    };

    request.send(body);
  });
}
